import { useCallback, useEffect, useState, type CSSProperties } from 'react'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  List,
  Modal,
  Progress,
  Select,
  Space,
  Spin,
  Typography,
  Upload,
  message,
} from 'antd'
import {
  ApiOutlined,
  EyeOutlined,
  FacebookOutlined,
  LogoutOutlined,
  UploadOutlined,
  DeleteOutlined,
  CreditCardOutlined,
  KeyOutlined,
} from '@ant-design/icons'
import { useAuth } from '../hooks/useAuth'
import { messengerWebhookBase, productName } from '../branding'
import { MessengerInboxCard } from '../components/MessengerInboxCard'
import { MessengerPageSubscriptionPanel } from '../components/MessengerPageSubscriptionPanel'
import {
  API_BASE,
  buyRequestPack,
  buyStoragePack,
  buyTokenPack,
  createCheckout,
  createOrRotateKbApiKey,
  deleteKbDocument,
  disconnectMetaPage,
  fetchPlan,
  getKbApiKey,
  getKbDocument,
  getKbUsage,
  getMetaConfig,
  listConnectedPages,
  listKbDocuments,
  listPendingPages,
  revokeKbApiKey,
  selectMetaPages,
  startMetaOAuth,
  updateChatModel,
  uploadKbCsv,
  type ConnectedFacebookPage,
  type MessengerKbApiKeyPublic,
  type MessengerKbPacks,
  type MessengerKbUsage,
} from '../services/api'

/** Matches API `MESSENGER_EXTRA_PAGE_MONTHLY_CENTS` / first page included. */
const EXTRA_PAGE_MONTHLY_CENTS = 2000
const INCLUDED_PAGES = 1

const DEFAULT_PACKS: MessengerKbPacks = {
  storagePackQty: 0,
  requestPackQty: 0,
  tokenPackQty: 0,
  storagePackMonthlyCents: 500,
  requestPackMonthlyCents: 500,
  tokenPackMonthlyCents: 500,
  storagePackBytes: 10 * 1024 * 1024,
  requestPackPerDay: 5000,
  tokenPackTokens: 1_000_000,
}

function formatMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return String(n)
}

function formatUsdFromCents(cents: number): string {
  const dollars = cents / 100
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`
}

function usagePercent(used: number, limit: number): number {
  if (limit <= 0) return 0
  return Math.min(100, Math.round((used / limit) * 100))
}

const { Title, Paragraph, Text } = Typography

type PackKind = 'storage' | 'request' | 'token'

export default function DashboardPage() {
  const { logout } = useAuth()
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [connectedPages, setConnectedPages] = useState<ConnectedFacebookPage[]>([])
  const [docs, setDocs] = useState<
    Array<{ id: string; title: string | null; chunkCount: number; createdAt: string }>
  >([])
  const [pendingPages, setPendingPages] = useState<Array<{ id: string; name: string }>>([])
  const [selectedPendingIds, setSelectedPendingIds] = useState<string[]>([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [detail, setDetail] = useState<Awaited<
    ReturnType<typeof getKbDocument>
  >['document'] | null>(null)
  const [apiKeyMeta, setApiKeyMeta] = useState<MessengerKbApiKeyPublic | null>(null)
  const [revealedApiKey, setRevealedApiKey] = useState<string | null>(null)
  const [usage, setUsage] = useState<MessengerKbUsage | null>(null)
  const [packs, setPacks] = useState<MessengerKbPacks>(DEFAULT_PACKS)
  const [planMonthlyCents, setPlanMonthlyCents] = useState(2000)

  const openDocument = async (documentId: string) => {
    setDetailLoading(true)
    try {
      const result = await getKbDocument(documentId)
      setDetail(result.document)
    } catch (e: unknown) {
      message.error(
        (e as { response?: { data?: { error?: string; message?: string } } }).response?.data
          ?.error ||
          (e as { response?: { data?: { message?: string } } }).response?.data?.message ||
          'Failed to load document'
      )
    } finally {
      setDetailLoading(false)
    }
  }

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const [cfg, kb] = await Promise.all([getMetaConfig(), listKbDocuments()])
      setDocs(kb.documents || [])
      if (cfg.pages) {
        setConnectedPages(cfg.pages)
      } else {
        try {
          const listed = await listConnectedPages()
          setConnectedPages(listed.pages || [])
        } catch {
          setConnectedPages(
            cfg.config?.metaPageId
              ? [
                  {
                    id: cfg.config.metaPageId,
                    metaPageId: cfg.config.metaPageId,
                    pageName: cfg.config.pageName || null,
                    webhookSlug: cfg.config.webhookSlug || null,
                    webhookUrl: cfg.config.webhookSlug
                      ? `${messengerWebhookBase()}/webhook/${cfg.config.webhookSlug}`
                      : null,
                    isActive: Boolean(cfg.config.isActive),
                    createdAt: new Date().toISOString(),
                  },
                ]
              : []
          )
        }
      }
      try {
        const pending = await listPendingPages()
        setPendingPages(pending.pages || [])
        setSelectedPendingIds([])
      } catch {
        setPendingPages([])
        setSelectedPendingIds([])
      }
      try {
        const plan = await fetchPlan()
        setPlanMonthlyCents(plan.monthlyFeeCents || Math.round(plan.basePrice * 100))
      } catch {
        /* keep default */
      }
      try {
        const [keyRes, usageRes] = await Promise.all([getKbApiKey(), getKbUsage()])
        setApiKeyMeta(keyRes.key)
        setUsage(usageRes.usage)
        setPacks({
          ...DEFAULT_PACKS,
          ...usageRes.packs,
          tokenPackMonthlyCents: usageRes.packs.tokenPackMonthlyCents ?? 500,
          storagePackBytes: usageRes.packs.storagePackBytes ?? DEFAULT_PACKS.storagePackBytes,
          requestPackPerDay: usageRes.packs.requestPackPerDay ?? DEFAULT_PACKS.requestPackPerDay,
          tokenPackTokens: usageRes.packs.tokenPackTokens ?? DEFAULT_PACKS.tokenPackTokens,
        })
      } catch {
        /* entitlement may block until checkout */
      }
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { message?: string } } }
      if (err.response?.status === 403) {
        message.warning(
          err.response.data?.message ||
            'Complete Stripe checkout to unlock Meta Messenger AI features.'
        )
      } else {
        message.error('Failed to load dashboard')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('checkout') === 'success') {
      message.success('Subscription active')
    }
    if (params.get('metaOAuth') === 'connected') {
      message.success('Facebook Page connected')
    }
    if (params.get('metaOAuth') === 'select_page') {
      message.info('Select a Facebook Page below')
    }
    if (params.get('metaOAuth') === 'error') {
      message.error(params.get('message') || 'Facebook connect failed')
    }
    void refresh()
  }, [refresh])

  const connectFacebook = async () => {
    setBusy(true)
    try {
      const returnTo = `${window.location.origin}/dashboard`
      const { authorizeUrl } = await startMetaOAuth(returnTo)
      window.location.href = authorizeUrl
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string }
      message.error(err.response?.data?.message || err.message || 'Connect failed')
      setBusy(false)
    }
  }

  const pageConnected = connectedPages.length > 0
  const extraPageCount = Math.max(0, connectedPages.length - INCLUDED_PAGES)
  const planFeeLabel = formatUsdFromCents(planMonthlyCents)
  const extraPageFeeLabel = formatUsdFromCents(EXTRA_PAGE_MONTHLY_CENTS)

  const storagePackQty = usage?.storagePackQty ?? packs.storagePackQty
  const requestPackQty = usage?.requestPackQty ?? packs.requestPackQty
  const tokenPackQty = usage?.tokenPackQty ?? packs.tokenPackQty
  const costSummary = {
    baseCents: planMonthlyCents,
    extraPagesCents: extraPageCount * EXTRA_PAGE_MONTHLY_CENTS,
    storageCents: storagePackQty * packs.storagePackMonthlyCents,
    requestCents: requestPackQty * packs.requestPackMonthlyCents,
    tokenCents: tokenPackQty * packs.tokenPackMonthlyCents,
    totalCents:
      planMonthlyCents +
      extraPageCount * EXTRA_PAGE_MONTHLY_CENTS +
      storagePackQty * packs.storagePackMonthlyCents +
      requestPackQty * packs.requestPackMonthlyCents +
      tokenPackQty * packs.tokenPackMonthlyCents,
    storagePackQty,
    requestPackQty,
    tokenPackQty,
  }

  const applyPackPurchaseResult = (res: { usage: MessengerKbUsage; packs: MessengerKbPacks }) => {
    setUsage(res.usage)
    setPacks({
      ...DEFAULT_PACKS,
      ...res.packs,
      tokenPackMonthlyCents: res.packs.tokenPackMonthlyCents ?? packs.tokenPackMonthlyCents,
      storagePackBytes: res.packs.storagePackBytes ?? packs.storagePackBytes,
      requestPackPerDay: res.packs.requestPackPerDay ?? packs.requestPackPerDay,
      tokenPackTokens: res.packs.tokenPackTokens ?? packs.tokenPackTokens,
    })
  }

  const confirmBuyPack = (kind: PackKind) => {
    if (!usage) {
      message.warning('Usage is not loaded yet. Complete checkout if you have not subscribed.')
      return
    }

    const priceCents =
      kind === 'storage'
        ? packs.storagePackMonthlyCents
        : kind === 'request'
          ? packs.requestPackMonthlyCents
          : packs.tokenPackMonthlyCents

    const currentQty =
      kind === 'storage'
        ? usage.storagePackQty
        : kind === 'request'
          ? usage.requestPackQty
          : usage.tokenPackQty

    const currentLimitLabel =
      kind === 'storage'
        ? formatMb(usage.storageLimitBytes)
        : kind === 'request'
          ? `${usage.requestLimitPerDay.toLocaleString()} requests/day`
          : `${formatTokens(usage.tokenLimitPerMonth)} tokens/month`

    const nextLimitLabel =
      kind === 'storage'
        ? formatMb(usage.storageLimitBytes + packs.storagePackBytes)
        : kind === 'request'
          ? `${(usage.requestLimitPerDay + packs.requestPackPerDay).toLocaleString()} requests/day`
          : `${formatTokens(usage.tokenLimitPerMonth + packs.tokenPackTokens)} tokens/month`

    const unitLabel =
      kind === 'storage'
        ? `+${formatMb(packs.storagePackBytes)} storage`
        : kind === 'request'
          ? `+${packs.requestPackPerDay.toLocaleString()} requests/day`
          : `+${formatTokens(packs.tokenPackTokens)} AI tokens/month`

    const title =
      kind === 'storage'
        ? 'Add storage pack'
        : kind === 'request'
          ? 'Add request pack'
          : 'Add token pack'

    const nextTotalCents = costSummary.totalCents + priceCents

    Modal.confirm({
      title,
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            This adds {unitLabel} for {formatUsdFromCents(priceCents)}/mo.
          </p>
          <p style={{ marginBottom: 8, color: '#475569' }}>
            Packs owned: {currentQty} → {currentQty + 1}
            <br />
            Limit: {currentLimitLabel} → {nextLimitLabel}
            <br />
            Estimated monthly total: {formatUsdFromCents(costSummary.totalCents)} →{' '}
            {formatUsdFromCents(nextTotalCents)}
          </p>
        </div>
      ),
      okText: `Confirm · ${formatUsdFromCents(priceCents)}/mo`,
      cancelText: 'Cancel',
      onOk: async () => {
        setBusy(true)
        try {
          const res =
            kind === 'storage'
              ? await buyStoragePack()
              : kind === 'request'
                ? await buyRequestPack()
                : await buyTokenPack()
          applyPackPurchaseResult(res)
          const newLimit =
            kind === 'storage'
              ? formatMb(res.usage.storageLimitBytes)
              : kind === 'request'
                ? `${res.usage.requestLimitPerDay.toLocaleString()} requests/day`
                : `${formatTokens(res.usage.tokenLimitPerMonth)} tokens/month`
          message.success(
            `${title.replace('Add', 'Added')} — new limit ${newLimit}`
          )
        } catch (e: unknown) {
          message.error(
            (e as { response?: { data?: { message?: string } } }).response?.data?.message ||
              'Purchase failed'
          )
          throw e
        } finally {
          setBusy(false)
        }
      },
    })
  }

  const name = productName()

  const packRowStyle: CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '12px 14px',
    marginBottom: 10,
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    background: '#f8fafc',
  }

  return (
    <div style={{ maxWidth: 880, margin: '0 auto', padding: '32px 16px 72px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ color: '#0f172a', margin: 0 }}>
          {name} dashboard
        </Title>
        <Button icon={<LogoutOutlined />} onClick={logout}>
          Log out
        </Button>
      </div>
      <Paragraph style={{ color: '#475569' }}>
        {planFeeLabel}/mo · Connect Facebook · Upload FAQ · AI answers Messenger chats
      </Paragraph>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card
          className="messenger-card"
          loading={loading}
          title="Usage & costs"
          style={{ background: '#ffffff', borderColor: '#cbd5e1' }}
        >
          <Paragraph style={{ color: '#334155', marginBottom: 12 }}>
            See what you use and what you pay. Packs raise your limits and add to your monthly total.
          </Paragraph>

          <div
            style={{
              marginBottom: 20,
              padding: 14,
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
            }}
          >
            <Text style={{ color: '#0f172a', fontWeight: 600, display: 'block', marginBottom: 8 }}>
              Estimated monthly total
            </Text>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ color: '#475569' }}>Plan base</Text>
              <Text style={{ color: '#0f172a' }}>{formatUsdFromCents(costSummary.baseCents)}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ color: '#475569' }}>
                Extra Facebook Pages ({extraPageCount} × {extraPageFeeLabel})
              </Text>
              <Text style={{ color: '#0f172a' }}>
                {formatUsdFromCents(costSummary.extraPagesCents)}
              </Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ color: '#475569' }}>
                Storage packs ({costSummary.storagePackQty} ×{' '}
                {formatUsdFromCents(packs.storagePackMonthlyCents)})
              </Text>
              <Text style={{ color: '#0f172a' }}>
                {formatUsdFromCents(costSummary.storageCents)}
              </Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ color: '#475569' }}>
                Request packs ({costSummary.requestPackQty} ×{' '}
                {formatUsdFromCents(packs.requestPackMonthlyCents)})
              </Text>
              <Text style={{ color: '#0f172a' }}>
                {formatUsdFromCents(costSummary.requestCents)}
              </Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text style={{ color: '#475569' }}>
                Token packs ({costSummary.tokenPackQty} ×{' '}
                {formatUsdFromCents(packs.tokenPackMonthlyCents)})
              </Text>
              <Text style={{ color: '#0f172a' }}>{formatUsdFromCents(costSummary.tokenCents)}</Text>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: 8,
                borderTop: '1px solid #cbd5e1',
              }}
            >
              <Text style={{ color: '#0f172a', fontWeight: 700 }}>Total</Text>
              <Text style={{ color: '#0f172a', fontWeight: 700 }}>
                {formatUsdFromCents(costSummary.totalCents)}/mo
              </Text>
            </div>
          </div>

          {usage ? (
            <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 20 }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={{ color: '#0f172a', fontWeight: 600 }}>Storage</Text>
                  <Text style={{ color: '#475569' }}>
                    {formatMb(usage.storageBytes)} / {formatMb(usage.storageLimitBytes)} ·{' '}
                    {usage.storagePackQty} pack{usage.storagePackQty === 1 ? '' : 's'}
                  </Text>
                </div>
                <Progress
                  percent={usagePercent(usage.storageBytes, usage.storageLimitBytes)}
                  status={usage.nearStorageLimit ? 'exception' : 'normal'}
                  strokeColor={usage.nearStorageLimit ? undefined : '#0f766e'}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Knowledge base text storage. Does not reset.
                </Text>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={{ color: '#0f172a', fontWeight: 600 }}>API requests today</Text>
                  <Text style={{ color: '#475569' }}>
                    {usage.requestsToday.toLocaleString()} /{' '}
                    {usage.requestLimitPerDay.toLocaleString()} · {usage.requestPackQty} pack
                    {usage.requestPackQty === 1 ? '' : 's'}
                  </Text>
                </div>
                <Progress
                  percent={usagePercent(usage.requestsToday, usage.requestLimitPerDay)}
                  status={usage.nearRequestLimit ? 'exception' : 'normal'}
                  strokeColor={usage.nearRequestLimit ? undefined : '#0f766e'}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Resets daily (UTC {usage.dayUtc}).
                </Text>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Text style={{ color: '#0f172a', fontWeight: 600 }}>AI chat tokens</Text>
                  {usage.usesPlatformOpenAiKey ? (
                    <Text style={{ color: '#475569' }}>
                      {formatTokens(usage.tokensUsedThisMonth)} /{' '}
                      {formatTokens(usage.tokenLimitPerMonth)} · {usage.tokenPackQty} pack
                      {usage.tokenPackQty === 1 ? '' : 's'}
                    </Text>
                  ) : (
                    <Text style={{ color: '#475569' }}>Not metered</Text>
                  )}
                </div>
                {usage.usesPlatformOpenAiKey ? (
                  <>
                    <Progress
                      percent={usagePercent(
                        usage.tokensUsedThisMonth,
                        usage.tokenLimitPerMonth
                      )}
                      status={usage.nearTokenLimit ? 'exception' : 'normal'}
                      strokeColor={usage.nearTokenLimit ? undefined : '#0f766e'}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Platform OpenAI key. Resets monthly (UTC {usage.monthUtc}).
                    </Text>
                  </>
                ) : (
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginTop: 4 }}
                    message="Using your own OpenAI key — chat tokens are not metered."
                  />
                )}
              </div>
            </Space>
          ) : (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Usage meters appear after your subscription is active."
            />
          )}

          <Text
            style={{
              color: '#0f172a',
              fontWeight: 600,
              display: 'block',
              marginBottom: 10,
            }}
          >
            Buy capacity packs
          </Text>

          <div style={packRowStyle}>
            <div style={{ flex: '1 1 220px' }}>
              <Text style={{ color: '#0f172a', fontWeight: 600, display: 'block' }}>
                Storage pack · {formatUsdFromCents(packs.storagePackMonthlyCents)}/mo
              </Text>
              <Text style={{ color: '#64748b', fontSize: 13 }}>
                Adds {formatMb(packs.storagePackBytes)}
                {usage
                  ? ` · limit ${formatMb(usage.storageLimitBytes)} → ${formatMb(usage.storageLimitBytes + packs.storagePackBytes)}`
                  : ''}
              </Text>
            </div>
            <Button loading={busy} disabled={!usage} onClick={() => confirmBuyPack('storage')}>
              Add storage pack · {formatUsdFromCents(packs.storagePackMonthlyCents)}/mo
            </Button>
          </div>

          <div style={packRowStyle}>
            <div style={{ flex: '1 1 220px' }}>
              <Text style={{ color: '#0f172a', fontWeight: 600, display: 'block' }}>
                Request pack · {formatUsdFromCents(packs.requestPackMonthlyCents)}/mo
              </Text>
              <Text style={{ color: '#64748b', fontSize: 13 }}>
                Adds {packs.requestPackPerDay.toLocaleString()} requests/day
                {usage
                  ? ` · limit ${usage.requestLimitPerDay.toLocaleString()} → ${(usage.requestLimitPerDay + packs.requestPackPerDay).toLocaleString()}/day`
                  : ''}
              </Text>
            </div>
            <Button loading={busy} disabled={!usage} onClick={() => confirmBuyPack('request')}>
              Add request pack · {formatUsdFromCents(packs.requestPackMonthlyCents)}/mo
            </Button>
          </div>

          <div style={{ ...packRowStyle, marginBottom: 16 }}>
            <div style={{ flex: '1 1 220px' }}>
              <Text style={{ color: '#0f172a', fontWeight: 600, display: 'block' }}>
                Token pack · {formatUsdFromCents(packs.tokenPackMonthlyCents)}/mo
              </Text>
              <Text style={{ color: '#64748b', fontSize: 13 }}>
                {usage && !usage.usesPlatformOpenAiKey
                  ? 'Only needed when using the platform OpenAI key'
                  : `Adds ${formatTokens(packs.tokenPackTokens)} tokens/month`}
                {usage && usage.usesPlatformOpenAiKey
                  ? ` · limit ${formatTokens(usage.tokenLimitPerMonth)} → ${formatTokens(usage.tokenLimitPerMonth + packs.tokenPackTokens)}`
                  : ''}
              </Text>
            </div>
            <Button
              loading={busy}
              disabled={!usage || !usage.usesPlatformOpenAiKey}
              onClick={() => confirmBuyPack('token')}
            >
              Add token pack · {formatUsdFromCents(packs.tokenPackMonthlyCents)}/mo
            </Button>
          </div>

          <Paragraph style={{ color: '#64748b', marginBottom: 12 }}>
            If checkout was interrupted, resume Stripe billing for {name}.
          </Paragraph>
          <Button
            icon={<CreditCardOutlined />}
            loading={busy}
            onClick={async () => {
              setBusy(true)
              try {
                const { checkoutUrl } = await createCheckout()
                window.location.href = checkoutUrl
              } catch (e: unknown) {
                message.error(
                  (e as { response?: { data?: { message?: string } } }).response?.data?.message ||
                    'Checkout failed'
                )
                setBusy(false)
              }
            }}
          >
            Open checkout / manage billing
          </Button>
        </Card>

        <Card
          className="messenger-card"
          loading={loading}
          title="Facebook Pages"
          style={{ background: '#ffffff', borderColor: '#cbd5e1' }}
        >
          <Alert
            type={pageConnected ? 'success' : 'info'}
            showIcon
            style={{ marginBottom: 16 }}
            message={
              pageConnected
                ? `${connectedPages.length} Page${connectedPages.length === 1 ? '' : 's'} connected`
                : 'Connect your Facebook Page in one click'
            }
            description={`First Page is included in ${planFeeLabel}/mo. Each extra Page is +${extraPageFeeLabel}/mo.`}
          />

          {connectedPages.length > 0 && (
            <List
              style={{ marginBottom: 16 }}
              dataSource={connectedPages}
              renderItem={(p) => (
                <List.Item
                  actions={[
                    <Button
                      key="disconnect"
                      type="link"
                      danger
                      loading={busy}
                      onClick={async () => {
                        setBusy(true)
                        try {
                          await disconnectMetaPage(p.metaPageId)
                          message.success('Page disconnected')
                          await refresh()
                        } catch (e: unknown) {
                          message.error(
                            (e as { response?: { data?: { message?: string } } }).response?.data
                              ?.message || 'Disconnect failed'
                          )
                        } finally {
                          setBusy(false)
                        }
                      }}
                    >
                      Disconnect
                    </Button>,
                  ]}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <List.Item.Meta
                      title={<Text style={{ color: '#0f172a' }}>{p.pageName || p.metaPageId}</Text>}
                      description={
                        <Text style={{ color: '#64748b' }}>
                          ID {p.metaPageId}
                          {p.webhookUrl ? ` · ${p.webhookUrl}` : ''}
                        </Text>
                      }
                    />
                    <Collapse
                      size="small"
                      items={[
                        {
                          key: 'subscription',
                          label: 'Page events & webhook activity',
                          children: (
                            <MessengerPageSubscriptionPanel
                              metaPageId={p.metaPageId}
                              pageName={p.pageName}
                            />
                          ),
                        },
                      ]}
                    />
                  </Space>
                </List.Item>
              )}
            />
          )}

          {extraPageCount > 0 && (
            <Paragraph style={{ color: '#475569' }}>
              Extra Page billing: {extraPageCount} × {extraPageFeeLabel}/mo ={' '}
              {formatUsdFromCents(extraPageCount * EXTRA_PAGE_MONTHLY_CENTS)}/mo
            </Paragraph>
          )}

          {pendingPages.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text style={{ color: '#0f172a', fontWeight: 600 }}>Select Pages to connect</Text>
              <Checkbox.Group
                style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}
                value={selectedPendingIds}
                onChange={(vals) => setSelectedPendingIds(vals as string[])}
                options={pendingPages.map((p) => ({ label: p.name, value: p.id }))}
              />
              <Button
                type="primary"
                style={{ marginTop: 12 }}
                loading={busy}
                disabled={selectedPendingIds.length === 0}
                onClick={async () => {
                  setBusy(true)
                  try {
                    const result = await selectMetaPages(selectedPendingIds)
                    message.success(
                      `Connected ${result.connected?.length ?? selectedPendingIds.length} Page(s)`
                    )
                    await refresh()
                  } catch (e: unknown) {
                    message.error(
                      (e as { response?: { data?: { message?: string } } }).response?.data
                        ?.message || 'Select failed'
                    )
                  } finally {
                    setBusy(false)
                  }
                }}
              >
                Connect selected
              </Button>
            </div>
          )}

          <Button
            type="primary"
            icon={<FacebookOutlined />}
            loading={busy}
            onClick={() => void connectFacebook()}
          >
            {pageConnected ? 'Add another Page' : 'Connect Facebook'}
          </Button>
        </Card>

        {pageConnected ? <MessengerInboxCard /> : null}

        <Card
          className="messenger-card"
          loading={loading}
          title="API access"
          style={{ background: '#ffffff', borderColor: '#cbd5e1' }}
        >
          <Paragraph style={{ color: '#334155' }}>
            Hydrate and manage your knowledge base remotely. Manage usage and buy packs in Usage
            &amp; costs above.
          </Paragraph>

          {usage && (
            <div style={{ marginBottom: 16 }}>
              <Text style={{ color: '#0f172a', fontWeight: 600, marginRight: 8 }}>Chat model</Text>
              <Select
                style={{ minWidth: 200 }}
                value={usage.chatModel}
                options={(usage.allowedChatModels || []).map((m) => ({
                  value: m,
                  label:
                    m === 'gpt-4o-mini'
                      ? 'gpt-4o-mini (recommended)'
                      : m === 'gpt-4o'
                        ? 'gpt-4o (higher quality)'
                        : m,
                }))}
                onChange={async (model) => {
                  setBusy(true)
                  try {
                    const res = await updateChatModel(model)
                    setUsage(res.usage)
                    message.success(`Chat model set to ${res.chatModel}`)
                  } catch (e: unknown) {
                    message.error(
                      (e as { response?: { data?: { message?: string } } }).response?.data
                        ?.message || 'Failed to update model'
                    )
                  } finally {
                    setBusy(false)
                  }
                }}
              />
            </div>
          )}

          <Space wrap style={{ marginBottom: 16 }}>
            <Button
              icon={<KeyOutlined />}
              loading={busy}
              onClick={async () => {
                setBusy(true)
                try {
                  const { key } = await createOrRotateKbApiKey()
                  setApiKeyMeta(key)
                  setRevealedApiKey(key.apiKey || null)
                  message.success(
                    key.apiKey
                      ? 'API key created — copy it now; it won’t be shown again'
                      : 'Key rotated'
                  )
                } catch (e: unknown) {
                  message.error(
                    (e as { response?: { data?: { message?: string } } }).response?.data
                      ?.message || 'Failed to create API key'
                  )
                } finally {
                  setBusy(false)
                }
              }}
            >
              {apiKeyMeta?.keyId ? 'Rotate API key' : 'Create API key'}
            </Button>
            {apiKeyMeta?.enabled && (
              <Button
                danger
                loading={busy}
                onClick={async () => {
                  setBusy(true)
                  try {
                    await revokeKbApiKey()
                    setRevealedApiKey(null)
                    setApiKeyMeta((prev) => (prev ? { ...prev, enabled: false } : prev))
                    message.success('API key revoked')
                  } catch (e: unknown) {
                    message.error(
                      (e as { response?: { data?: { message?: string } } }).response?.data
                        ?.message || 'Revoke failed'
                    )
                  } finally {
                    setBusy(false)
                  }
                }}
              >
                Revoke
              </Button>
            )}
          </Space>

          {apiKeyMeta?.keyId && (
            <Paragraph style={{ color: '#64748b', marginBottom: 8 }}>
              Key id <Text code>{apiKeyMeta.keyId}</Text>
              {apiKeyMeta.lastFour ? ` · ends in ${apiKeyMeta.lastFour}` : ''}
              {apiKeyMeta.enabled ? '' : ' · revoked'}
            </Paragraph>
          )}

          {revealedApiKey && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message="Copy your API key now"
              description={<Text copyable code>{revealedApiKey}</Text>}
            />
          )}

          <Paragraph style={{ color: '#475569', marginBottom: 8 }}>
            <ApiOutlined /> Example upsert:
          </Paragraph>
          <pre
            style={{
              margin: 0,
              padding: 12,
              borderRadius: 8,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              color: '#0f172a',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: 12,
            }}
          >{`curl -X PUT "${API_BASE}/api/v1/messenger-kb/documents/faq-hours" \\
  -H "Authorization: Bearer mk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{"question":"What are your hours?","answer":"Mon–Fri 9–5"}'`}</pre>
        </Card>

        <Card
          className="messenger-card"
          loading={loading}
          title="Knowledge base"
          style={{ background: '#ffffff', borderColor: '#cbd5e1' }}
        >
          <Paragraph style={{ color: '#334155' }}>
            Upload a CSV with columns <code>question</code>, <code>answer</code> (optional{' '}
            <code>topic</code>, <code>source_id</code>).
          </Paragraph>
          <Upload
            accept=".csv,text/csv"
            showUploadList={false}
            beforeUpload={async (file) => {
              setBusy(true)
              try {
                const result = await uploadKbCsv(file as File)
                message.success(`Imported ${result.imported ?? 0} FAQ rows`)
                await refresh()
              } catch (e: unknown) {
                message.error(
                  (e as { response?: { data?: { error?: string; message?: string } } }).response
                    ?.data?.error ||
                    (e as { response?: { data?: { message?: string } } }).response?.data?.message ||
                    'Upload failed'
                )
              } finally {
                setBusy(false)
              }
              return false
            }}
          >
            <Button icon={<UploadOutlined />} loading={busy}>
              Upload FAQ CSV
            </Button>
          </Upload>

          <List
            style={{ marginTop: 20 }}
            dataSource={docs}
            locale={{ emptyText: 'No documents yet' }}
            renderItem={(doc) => (
              <List.Item
                actions={[
                  <Button
                    key="view"
                    type="text"
                    icon={<EyeOutlined />}
                    onClick={() => void openDocument(doc.id)}
                  >
                    View
                  </Button>,
                  <Button
                    key="del"
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={async () => {
                      await deleteKbDocument(doc.id)
                      message.success('Deleted')
                      await refresh()
                    }}
                  />,
                ]}
              >
                <List.Item.Meta
                  title={<Text style={{ color: '#0f172a' }}>{doc.title || doc.id}</Text>}
                  description={
                    <Text style={{ color: '#64748b' }}>
                      {doc.chunkCount} chunks · {new Date(doc.createdAt).toLocaleString()}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      </Space>

      <Modal
        title={detail?.title || 'Knowledge document'}
        open={!!detail || detailLoading}
        onCancel={() => setDetail(null)}
        footer={[
          <Button key="close" type="primary" onClick={() => setDetail(null)}>
            Close
          </Button>,
        ]}
        width={720}
        destroyOnClose
      >
        <Spin spinning={detailLoading}>
          {detail && (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text type="secondary">Full document</Text>
                <pre
                  style={{
                    marginTop: 8,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 220,
                    overflow: 'auto',
                    padding: 12,
                    borderRadius: 8,
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    color: '#0f172a',
                  }}
                >
                  {detail.rawContent}
                </pre>
              </div>
              <div>
                <Text type="secondary">
                  Chunks ({detail.chunks.length}) — what search retrieves
                </Text>
                <List
                  style={{ marginTop: 8 }}
                  size="small"
                  dataSource={detail.chunks}
                  locale={{ emptyText: 'No chunks' }}
                  renderItem={(chunk) => (
                    <List.Item>
                      <List.Item.Meta
                        title={`Chunk ${chunk.chunkIndex + 1}`}
                        description={
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              fontFamily: 'inherit',
                              color: '#334155',
                            }}
                          >
                            {chunk.text}
                          </pre>
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
            </Space>
          )}
        </Spin>
      </Modal>
    </div>
  )
}
